import { useState } from 'react';
import { Plus, Pencil, Trash2, Check, X } from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  EXPENSE_CATEGORIES,
  EXPENSE_FREQUENCIES,
  type CompanyExpense,
  type ExpenseCategory,
  type ExpenseFrequency,
  type ExpenseInput
} from '@/hooks/useCompanyExpenses';
import { ExpenseDateField, type DateMode } from './ExpenseDateField';
import { useTimezoneUtils } from '@/hooks/useTimezoneUtils';
import type { ExpenseGroup } from './expenseGroups';
import { useCompanyLocale } from '@/hooks/useCompanyLocale';

interface ExpenseGroupSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  group: ExpenseGroup | null;
  expenses: CompanyExpense[];
  monthlyTotal: number;
  calculateAmount: (expense: CompanyExpense) => number;
  onAdd: (data: ExpenseInput) => void;
  onUpdate: (id: string, data: ExpenseInput) => void;
  onDelete: (id: string) => void;
  isAdding: boolean;
  isUpdating: boolean;
  selectedMonth?: string;
  periodLabel?: string | null;
}

export const ExpenseGroupSheet = ({
  open,
  onOpenChange,
  group,
  expenses,
  monthlyTotal,
  calculateAmount,
  onAdd,
  onUpdate,
  onDelete,
  isAdding,
  isUpdating,
  selectedMonth,
  periodLabel,
}: ExpenseGroupSheetProps) => {
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const { formatDate } = useTimezoneUtils();
  const { formatMoney: formatCurrency } = useCompanyLocale();

  // New expense form state
  const [newExpense, setNewExpense] = useState({
    name: '',
    amount: '',
    category: group?.categories[0] || 'other' as ExpenseCategory,
    frequency: 'monthly' as ExpenseFrequency,
  });

  // Edit expense form state
  const [editForm, setEditForm] = useState<{
    name: string;
    amount: string;
    frequency: ExpenseFrequency;
    is_active: boolean;
    dateMode: DateMode;
    start_date: string;
    selectedWeek: string;
  } | null>(null);

  const resetAddForm = () => {
    setNewExpense({
      name: '',
      amount: '',
      category: group?.categories[0] || 'other' as ExpenseCategory,
      frequency: 'monthly',
    });
    setIsAddingNew(false);
  };

  const handleAddSubmit = () => {
    if (!newExpense.name || !newExpense.amount) return;
    onAdd({
      name: newExpense.name,
      amount: parseFloat(newExpense.amount),
      category: newExpense.category,
      frequency: newExpense.frequency,
      is_active: true,
    });
    resetAddForm();
  };

  const handleEditStart = (expense: CompanyExpense) => {
    setEditingId(expense.id);
    setEditForm({
      name: expense.name,
      amount: expense.amount.toString(),
      frequency: expense.frequency,
      is_active: expense.is_active,
      dateMode: expense.start_date ? 'date' : 'date',
      start_date: expense.start_date || '',
      selectedWeek: expense.start_date || '',
    });
  };

  const handleEditSubmit = (expense: CompanyExpense) => {
    if (!editForm || !editForm.name || !editForm.amount) return;
    onUpdate(expense.id, {
      ...expense,
      name: editForm.name,
      amount: parseFloat(editForm.amount),
      frequency: editForm.frequency,
      is_active: editForm.is_active,
      start_date: editForm.start_date || undefined,
    });
    setEditingId(null);
    setEditForm(null);
  };

  const handleEditCancel = () => {
    setEditingId(null);
    setEditForm(null);
  };

  const handleDelete = (id: string) => {
    if (confirm('Delete this expense?')) {
      onDelete(id);
    }
  };

  // Reset state when sheet closes
  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      setIsAddingNew(false);
      setEditingId(null);
      setEditForm(null);
    }
    onOpenChange(isOpen);
  };

  if (!group) return null;

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetContent className="w-full sm:max-w-lg flex flex-col">
        <SheetHeader className="space-y-1">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-primary/10 text-primary">
              {group.icon}
            </div>
            <div>
              <SheetTitle>{group.label}</SheetTitle>
              <SheetDescription>
                {periodLabel} · {formatCurrency(monthlyTotal)}
              </SheetDescription>
            </div>
          </div>
        </SheetHeader>

        <ScrollArea className="flex-1 -mx-6 px-6 mt-4">
          <div className="space-y-2">
            {/* Expense list */}
            {expenses.length > 0 ? (
              expenses.map(expense => {
                const periodAmount = calculateAmount(expense);
                const isEditing = editingId === expense.id;
                const freqLabel = EXPENSE_FREQUENCIES.find(f => f.value === expense.frequency)?.label;

                if (isEditing && editForm) {
                  return (
                    <div key={expense.id} className="p-4 rounded-lg border bg-muted/30">
                      <div className="space-y-3">
                        <div>
                          <label className="text-xs text-muted-foreground mb-1 block">Name</label>
                          <Input
                            value={editForm.name}
                            onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                            className="h-9"
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="text-xs text-muted-foreground mb-1 block">Amount</label>
                            <Input
                              type="number"
                              value={editForm.amount}
                              onChange={(e) => setEditForm({ ...editForm, amount: e.target.value })}
                              className="h-9"
                            />
                          </div>
                          <div>
                            <label className="text-xs text-muted-foreground mb-1 block">Frequency</label>
                            <Select
                              value={editForm.frequency}
                              onValueChange={(v) => setEditForm({ ...editForm, frequency: v as ExpenseFrequency })}
                            >
                              <SelectTrigger className="h-9">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {EXPENSE_FREQUENCIES.map(freq => (
                                  <SelectItem key={freq.value} value={freq.value}>{freq.label}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Switch
                            checked={editForm.is_active}
                            onCheckedChange={(checked) => setEditForm({ ...editForm, is_active: checked })}
                          />
                          <span className="text-sm text-muted-foreground">Active</span>
                        </div>
                        <ExpenseDateField
                          dateMode={editForm.dateMode}
                          onDateModeChange={(mode) => setEditForm({ ...editForm, dateMode: mode })}
                          startDate={editForm.start_date}
                          onStartDateChange={(date) => setEditForm({ ...editForm, start_date: date })}
                          selectedWeek={editForm.selectedWeek}
                          onSelectedWeekChange={(week) => setEditForm({ ...editForm, selectedWeek: week })}
                          compact
                        />
                        <div className="flex justify-end gap-2 pt-2">
                          <Button size="sm" variant="ghost" onClick={handleEditCancel}>
                            <X className="h-4 w-4 mr-1" />
                            Cancel
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => handleEditSubmit(expense)}
                            disabled={isUpdating}
                          >
                            <Check className="h-4 w-4 mr-1" />
                            Save
                          </Button>
                        </div>
                      </div>
                    </div>
                  );
                }

                return (
                  <div
                    key={expense.id}
                    className={`flex items-center justify-between p-4 rounded-lg border hover:bg-muted/30 transition-colors ${!expense.is_active ? 'opacity-50' : ''}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">{expense.name}</div>
                      <div className="text-sm text-muted-foreground">
                        {formatCurrency(expense.amount)} {freqLabel?.toLowerCase()}
                        {expense.start_date && (
                          <span className="ml-2 text-xs">· {formatDate(expense.start_date, 'MMM d, yyyy')}</span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="text-right">
                        <div className="font-semibold">{formatCurrency(periodAmount)}</div>
                        <div className="text-xs text-muted-foreground">
                          {expense.frequency === 'one_time' ? 'one-time' : '/mo'}
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => handleEditStart(expense)}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-destructive hover:text-destructive"
                          onClick={() => handleDelete(expense.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <p className="mb-2">No expenses in this category</p>
                <p className="text-sm">Click the button below to add one</p>
              </div>
            )}
          </div>
        </ScrollArea>

        {/* Add new expense form */}
        <div className="border-t pt-4 mt-4">
          {isAddingNew ? (
            <div className="space-y-3">
              {group.categories.length > 1 && (
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">Category</label>
                  <Select
                    value={newExpense.category}
                    onValueChange={(v) => setNewExpense({ ...newExpense, category: v as ExpenseCategory })}
                  >
                    <SelectTrigger className="h-9">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {group.categories.map(cat => {
                        const label = EXPENSE_CATEGORIES.find(c => c.value === cat)?.label;
                        return (
                          <SelectItem key={cat} value={cat}>{label}</SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">Name</label>
                <Input
                  value={newExpense.name}
                  onChange={(e) => setNewExpense({ ...newExpense, name: e.target.value })}
                  placeholder="e.g., Office Manager"
                  className="h-9"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">Amount ($)</label>
                  <Input
                    type="number"
                    value={newExpense.amount}
                    onChange={(e) => setNewExpense({ ...newExpense, amount: e.target.value })}
                    placeholder="0"
                    className="h-9"
                  />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">Frequency</label>
                  <Select
                    value={newExpense.frequency}
                    onValueChange={(v) => setNewExpense({ ...newExpense, frequency: v as ExpenseFrequency })}
                  >
                    <SelectTrigger className="h-9">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {EXPENSE_FREQUENCIES.map(freq => (
                        <SelectItem key={freq.value} value={freq.value}>{freq.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {periodLabel && (
                <div className="text-sm text-muted-foreground flex items-center gap-2">
                  <span>📅 Adding to: {periodLabel}</span>
                </div>
              )}
              <div className="flex justify-end gap-2">
                <Button variant="ghost" size="sm" onClick={resetAddForm}>
                  Cancel
                </Button>
                <Button
                  size="sm"
                  onClick={handleAddSubmit}
                  disabled={isAdding || !newExpense.name || !newExpense.amount}
                >
                  Add Expense
                </Button>
              </div>
            </div>
          ) : (
            <Button
              variant="outline"
              className="w-full"
              onClick={() => setIsAddingNew(true)}
            >
              <Plus className="h-4 w-4 mr-2" />
              {group.addLabel}
            </Button>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};
